import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, ExternalLink, Eye, EyeOff, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Settings = () => {
  const [settings, setSettings] = useState({
    google_sheet_id: "",
    google_sheet_tab: "Sheet1",
    google_sheets_webapp_url: "",
    zapier_parent_webhook_url: "",
    zapier_tshirt_webhook_url: "",
    tshirt_recipient_email: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showUrls, setShowUrls] = useState({
    webapp: false,
    parent: false,
    tshirt: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleShow = (field) => {
    setShowUrls(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 fade-in max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-stone-900 tracking-tight">Settings</h1>
        <p className="text-sm sm:text-base text-stone-500 mt-1">Configure integrations and webhook endpoints</p>
      </div>

      {/* CampMinder Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading flex items-center gap-2">
            CampMinder API
            <CheckCircle2 className="w-5 h-5 text-signal-success" />
          </CardTitle>
          <CardDescription>
            CampMinder credentials are configured via environment variables for security
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-stone-500">Client ID</Label>
              <p className="text-lg font-mono mt-1">241</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-stone-500">API Status</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="w-2 h-2 rounded-full bg-signal-success" />
                <span className="text-sm text-stone-600">Configured via .env</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            To update CampMinder credentials, modify the backend .env file with CAMPMINDER_SUBSCRIPTION_KEY
          </p>
        </CardContent>
      </Card>

      {/* Google Sheets Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">Google Sheets</CardTitle>
          <CardDescription>
            Configure the target spreadsheet for enrollee data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4">
            <div>
              <Label htmlFor="sheet-id" className="text-sm font-medium">
                Spreadsheet ID
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="sheet-id"
                  value={settings.google_sheet_id}
                  onChange={(e) => handleChange("google_sheet_id", e.target.value)}
                  placeholder="1g_pTpFrtyJruapEBBdm3Fp73z_-Xg19n3fMPcIYvJWs"
                  className="font-mono text-sm"
                  data-testid="sheet-id-input"
                />
                {settings.google_sheet_id && (
                  <Button variant="outline" size="icon" asChild>
                    <a 
                      href={`https://docs.google.com/spreadsheets/d/${settings.google_sheet_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
              <p className="text-xs text-stone-500 mt-1">
                Found in the URL: /spreadsheets/d/<strong>SPREADSHEET_ID</strong>/edit
              </p>
            </div>

            <div>
              <Label htmlFor="sheet-tab" className="text-sm font-medium">
                Sheet Tab Name
              </Label>
              <Input
                id="sheet-tab"
                value={settings.google_sheet_tab}
                onChange={(e) => handleChange("google_sheet_tab", e.target.value)}
                placeholder="Sheet1"
                className="mt-1.5"
                data-testid="sheet-tab-input"
              />
            </div>

            <div>
              <Label htmlFor="webapp-url" className="text-sm font-medium">
                Apps Script Web App URL
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="webapp-url"
                  type={showUrls.webapp ? "text" : "password"}
                  value={settings.google_sheets_webapp_url}
                  onChange={(e) => handleChange("google_sheets_webapp_url", e.target.value)}
                  placeholder="https://script.google.com/macros/s/..."
                  className="font-mono text-sm"
                  data-testid="webapp-url-input"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => toggleShow("webapp")}
                >
                  {showUrls.webapp ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zapier Webhooks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-heading">Zapier Webhooks</CardTitle>
          <CardDescription>
            Configure webhook URLs for automated workflows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="parent-webhook" className="text-sm font-medium">
              Parent Email Webhook URL
            </Label>
            <p className="text-xs text-stone-500 mb-1.5">
              Triggered for each new enrollee after Google Sheets sync
            </p>
            <div className="flex gap-2">
              <Input
                id="parent-webhook"
                type={showUrls.parent ? "text" : "password"}
                value={settings.zapier_parent_webhook_url}
                onChange={(e) => handleChange("zapier_parent_webhook_url", e.target.value)}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                className="font-mono text-sm"
                data-testid="parent-webhook-input"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => toggleShow("parent")}
              >
                {showUrls.parent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <Separator />

          <div>
            <Label htmlFor="tshirt-webhook" className="text-sm font-medium">
              T-Shirt Distribution Webhook URL
            </Label>
            <p className="text-xs text-stone-500 mb-1.5">
              Triggered at 9:00 AM Eastern with daily digest of new enrollees
            </p>
            <div className="flex gap-2">
              <Input
                id="tshirt-webhook"
                type={showUrls.tshirt ? "text" : "password"}
                value={settings.zapier_tshirt_webhook_url}
                onChange={(e) => handleChange("zapier_tshirt_webhook_url", e.target.value)}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                className="font-mono text-sm"
                data-testid="tshirt-webhook-input"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => toggleShow("tshirt")}
              >
                {showUrls.tshirt ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <Separator />

          <div>
            <Label htmlFor="tshirt-email" className="text-sm font-medium">
              T-Shirt Digest Recipient Email
            </Label>
            <p className="text-xs text-stone-500 mb-1.5">
              Email address included in the t-shirt digest webhook payload for Zapier to send notifications to
            </p>
            <Input
              id="tshirt-email"
              type="email"
              value={settings.tshirt_recipient_email}
              onChange={(e) => handleChange("tshirt_recipient_email", e.target.value)}
              placeholder="distributor@example.com"
              className="mt-1.5"
              data-testid="tshirt-email-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-forest hover:bg-forest-dark text-white font-bold py-2 px-8 rounded-md shadow-lg transition-all flex items-center gap-2"
          data-testid="save-settings-btn"
        >
          <Save className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};
